import * as df from 'date-fns';
import React, { useState, useEffect, useCallback } from 'react';
import Calendar from '../../../../../molecules/merchant/resources/book/InputBookingDateTimeCalendar';
import Marubatsu from '../../../../../molecules/merchant/resources/book/InputBookingDateTimeMarubatsu';
import Preview from '../../../../../molecules/merchant/resources/book/InputBookingTargetPreview';

import {
  TimeSlotAvailability,
  SettingBookingDateTimes,
} from '../../../../../interfaces/merchants/resources/books';
import {
  clearCalendar,
  setAvailableDates,
  setAvailableTimes,
  setCalendar,
} from '../../../../../redux/actions/merchants/resources/books';
import {
  clearBookingTargets,
  inputBookingDateTime,
  RestartReservationError,
} from '../../../../../redux/actions/merchants/resources/books/inputs';
import { ReduxState } from '../../../../../redux/reducers';
import { getMerchantResourcesBooksInputs } from '../../../../../redux/selectors/merchants/resources/books/inputs';

import { scrollToTop } from '../../../../../hooks/useScrollToTop';
import { useShallowEqualSelector } from '../../../../../hooks/redux';
import { useAppDispatch } from '../../../../../redux';

interface ElementProps {
  settings: SettingBookingDateTimes;
  postable: boolean;
  backToModifyPage: () => void;
  onSubmit: () => void;
  onRestart: () => void;
  resourcePublicId: string;
}

type Props = ElementProps;

const DISPLAY_DATES = 6;

const InputBookingDateTime: React.FC<Props> = (props) => {
  const dispatch = useAppDispatch();
  const {
    settings: {
      firstSelected,
      events,
      availableDates,
      availableTimes,
      candidateViewType,
      selectedDate,
    },
    postable,
    resourcePublicId,
  } = props;

  const useSelector = () =>
    useShallowEqualSelector((state: ReduxState) => ({
      inputs: getMerchantResourcesBooksInputs(
        state.merchantsResourcesBooksInputs,
      ),
    }));
  const { inputs } = useSelector();
  const { course, options, staff } = inputs;

  const initialBaseDate =
    selectedDate && df.isValid(df.parse(selectedDate))
      ? df.parse(selectedDate)
      : new Date();

  const [loading, setLoading] = useState<boolean>(false);
  const [dates, setDates] = useState<Date[]>([]);
  const [baseDate, setBaseDate] = useState<Date>(initialBaseDate);

  useEffect(() => {
    scrollToTop();
    dispatch(clearCalendar());
    const { candidateViewType } = props.settings;
    if (candidateViewType === 'board') {
      _setCalendarFrom(baseDate);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const _setCalendar = useCallback(
    async (dates: Date[]) => {
      const { merchantId, course, options, staff } = inputs;

      if (!course || !options || !merchantId) {
        return;
      }

      setLoading(true);
      setDates(dates);

      await dispatch(
        setCalendar({
          course,
          options,
          merchantId,
          resourceId: resourcePublicId,
          staff,
          start: dates[0],
          end: dates[dates.length - 1],
        }),
      );
      setLoading(false);
    },
    [inputs, dispatch, resourcePublicId],
  );

  const _setMonthlyCalendarDate = useCallback(
    async (date: Date) => {
      const { merchantId, course, options, staff } = inputs;
      if (!course || !options || !merchantId) {
        return;
      }
      setLoading(true);
      setBaseDate(date);
      await dispatch(
        setAvailableDates({
          merchantId,
          resourceId: resourcePublicId,
          course,
          options,
          staff,
          date,
        }),
      );
      setLoading(false);
      setBaseDate(date);
    },
    [dispatch, inputs, resourcePublicId],
  );

  const handleMonthlyChangeDate = useCallback(
    (date: Date) => {
      _setMonthlyCalendarDate(date);
    },
    [_setMonthlyCalendarDate],
  );

  const _setMonthlyCalendarTime = useCallback(
    async (date: Date) => {
      const { merchantId, course, options, staff } = inputs;
      if (!course || !options || !merchantId) {
        return;
      }
      setLoading(true);
      await dispatch(
        setAvailableTimes({
          merchantId,
          resourceId: resourcePublicId,
          course,
          options,
          staff,
          date,
        }),
      );
      setLoading(false);
    },
    [dispatch, inputs, resourcePublicId],
  );

  const handleMonthlyChangeTime = useCallback(
    (date: Date) => {
      _setMonthlyCalendarTime(date);
    },
    [_setMonthlyCalendarTime],
  );

  const _setCalendarFrom = useCallback(
    (date: Date) => {
      const dates = df.eachDay(date, df.addDays(date, DISPLAY_DATES));
      _setCalendar(dates);
    },
    [_setCalendar],
  );

  const handleClickToday = useCallback(() => {
    const today = new Date();
    _setCalendarFrom(today);
  }, [_setCalendarFrom]);

  const handleClickNext = useCallback(() => {
    _setCalendarFrom(df.addDays(dates[0], DISPLAY_DATES + 1));
  }, [_setCalendarFrom, dates]);

  const handleClickPrev = useCallback(() => {
    const assumedPreviousDate = df.subDays(dates[0], DISPLAY_DATES + 1);
    const today = new Date();
    _setCalendarFrom(
      df.isBefore(assumedPreviousDate, today) ? today : assumedPreviousDate,
    );
  }, [dates, _setCalendarFrom]);

  const handleSubmit = useCallback(
    async (timeSlotAvailability: TimeSlotAvailability) => {
      const { onSubmit, onRestart } = props;
      try {
        await dispatch(
          inputBookingDateTime(
            inputs.reservationId!,
            timeSlotAvailability,
            inputs.merchantId ?? '',
          ),
        );
        onSubmit();
      } catch (e) {
        if (e instanceof RestartReservationError) {
          onRestart();
        }
      }
    },
    [dispatch, props, inputs.reservationId, inputs.merchantId],
  );

  const handleChangeTarget = useCallback(
    (targets: { course: boolean; options: boolean; staff?: boolean }) => {
      dispatch(clearBookingTargets(targets));
      props.backToModifyPage();
    },
    [props, dispatch],
  );

  if (!course || !options) {
    return null;
  }

  return (
    <div>
      <Preview
        firstSelected={firstSelected}
        course={course}
        options={options}
        staff={staff}
        onChangeTarget={handleChangeTarget}
      />
      {candidateViewType === 'board' ? (
        <Marubatsu
          loading={loading}
          displayDates={dates}
          events={events}
          postable={postable}
          onSubmit={handleSubmit}
          onClickToday={handleClickToday}
          onClickPrev={handleClickPrev}
          onClickNext={handleClickNext}
        />
      ) : (
        <Calendar
          baseDate={baseDate}
          availableDates={availableDates}
          availableTimes={availableTimes}
          postable={postable}
          onSubmit={handleSubmit}
          onChangeBaseDate={handleMonthlyChangeDate}
          onChangeSelectedDate={handleMonthlyChangeTime}
          selectedDate={selectedDate}
        />
      )}
    </div>
  );
};

export default InputBookingDateTime;
